/**
 * Per-rashi deep-dive extensions (classical Parashari pedagogy + standard references).
 * Base facts (lord, element, varna) come from jyotishaRashiChart.js.
 */

/** @typedef {{ hi: string; en: string }} Bi */
/** @typedef {{ qualityHi: string; qualityEn: string; exprHi: string; exprEn: string }} LordRow */

/** @type {Record<number, {
 *   sanskritMeaning: Bi;
 *   introLead: Bi;
 *   symbolism: Bi[];
 *   mythology: Bi[];
 *   elementPositive: Bi[];
 *   elementNegative: Bi[];
 *   swabhavWhy: Bi;
 *   swabhavExamples: Bi[];
 *   lordRows: LordRow[];
 *   appearance: Bi[];
 *   strengths: Bi[];
 *   challenges: Bi[];
 *   kalapurushWhy: Bi[];
 *   purusharthas: Bi[];
 *   relationships: { friendship: Bi; love: Bi; conflict: Bi; leadership: Bi };
 *   planets: Record<string, Bi>;
 *   dignityNotes: Bi[];
 *   lifeStages: { childhood: Bi; career: Bi; leadership: Bi; business: Bi; sports: Bi; spirituality: Bi };
 *   lagnaVsMoon: { lagna: Bi; moon: Bi; sun: Bi };
 *   archetypes: Bi[];
 * }>} */
export const RASHI_DEEP_DIVE_EXTENSIONS = {
  1: {
    sanskritMeaning: {
      hi: 'मेष = मेढ़ा (नर भेड़)। शास्त्र में अज (अजन्म) भी कहा जाता है - अनादि ऊर्जा जो सृष्टि आरंभ करती है।',
      en: 'Mesha means the ram (male sheep). Classical texts also link it to Aja - “the unborn,” primordial energy that initiates creation.',
    },
    introLead: {
      hi: 'राशि-चक्र की पहली राशि - वसंत-साम्य के निकट पूर्व दिशा में उदय की ऊर्जा। 0° – 30° साइडियरल मेष।',
      en: 'The first sign of the zodiac - dawn energy rising in the east. Spans 0° – 30° sidereal Aries.',
    },
    symbolism: [
      { hi: 'मेढ़ा सीधे सिर से टकराता है - पहले कदम, साहस, नेतृत्व।', en: 'The ram charges head-first - first step, courage, leadership.' },
      { hi: 'अग्नि का वाहन - यज्ञ की अग्नि आगे बढ़ाने वाला वाहन (वैदिक परंपरा)।', en: 'Vehicle of Agni - carrier of sacred fire in Vedic tradition.' },
      { hi: 'भयरहित अग्रणी - नया मार्ग खोलने की प्रवृत्ति।', en: 'Fearless pioneer - tendency to open new paths.' },
      { hi: 'आवेग और तीव्र प्रतिक्रिया - मंगल की प्रकृति।', en: 'Impulse and quick reaction - Mars nature.' },
    ],
    mythology: [
      { hi: 'मेष अग्नि तत्त्व से जुड़ा - शुद्धि, यज्ञ और आरंभ का प्रतीक।', en: 'Mesha ties to Agni tattva - purification, sacrifice and initiation.' },
      { hi: 'मंगल कार्तिकेय/हनुमान परंपरा से जुड़ा - वीरता और रक्षा।', en: 'Mars links to Kartikeya/Hanuman traditions - valor and protection.' },
      { hi: 'BPHS व मंत्रेश्वर: मेष साहस, नेतृत्व और कर्म-प्रधानता दर्शाता है।', en: 'BPHS & Mantreshwara: Mesha shows courage, leadership and action-orientation.' },
    ],
    elementPositive: [
      { hi: 'प्रेरणा और उत्साह', en: 'Inspiration and enthusiasm' },
      { hi: 'नेतृत्व और पहल', en: 'Leadership and initiative' },
      { hi: 'साहस और ऊर्जा', en: 'Courage and vitality' },
    ],
    elementNegative: [
      { hi: 'क्रोध और अधीरता', en: 'Anger and impatience' },
      { hi: 'आवेग में निर्णय', en: 'Impulsive decisions' },
      { hi: 'अतिस्पर्धा', en: 'Excess competition' },
    ],
    swabhavWhy: {
      hi: 'चर (चल) स्वभाव - मेष क्रिया शुरू करता है, गति पसंद करता है, स्थिर नहीं रुकता।',
      en: 'Chara (movable) nature - Mesha initiates action, prefers movement, rarely stays still.',
    },
    swabhavExamples: [
      { hi: 'नई परियोजना तुरंत शुरू करना', en: 'Starting a new project immediately' },
      { hi: 'यात्रा या स्थान बदलने की इच्छा', en: 'Urge to travel or change place' },
      { hi: 'प्रतीक्षा करने में बेचैनी', en: 'Restlessness while waiting' },
    ],
    lordRows: [
      { qualityHi: 'साहस', qualityEn: 'Courage', exprHi: 'पहल लेता है', exprEn: 'Takes initiative' },
      { qualityHi: 'ऊर्जा', qualityEn: 'Energy', exprHi: 'तेज़ कार्रवाई', exprEn: 'Fast action' },
      { qualityHi: 'प्रतिस्पर्धा', qualityEn: 'Competition', exprHi: 'जीतने की इच्छा', exprEn: 'Wants to win' },
      { qualityHi: 'आक्रामकता', qualityEn: 'Drive', exprHi: 'त्वरित प्रतिक्रिया', exprEn: 'Quick reactions' },
    ],
    appearance: [
      { hi: 'मांसपेशीय या एथलेटिक बनावट (प्रवृत्ति)', en: 'Muscular or athletic build (tendency)' },
      { hi: 'तीक्ष्ण या स्पष्ट विशेषताएँ', en: 'Sharp or defined features' },
      { hi: 'माथा या भौंहें प्रभावशाली', en: 'Prominent forehead or brows' },
      { hi: 'तेज़ चाल', en: 'Fast walk' },
    ],
    strengths: [
      { hi: 'बहादुर और स्वतंत्र', en: 'Brave and independent' },
      { hi: 'ऊर्जावान और उद्यमी', en: 'Energetic and entrepreneurial' },
      { hi: 'स्पष्ट और सीधा', en: 'Direct and straightforward' },
    ],
    challenges: [
      { hi: 'आवेगी निर्णय', en: 'Impulsive decisions' },
      { hi: 'अल्पकालिक क्रोध', en: 'Short temper' },
      { hi: 'अधीरता और अहंकार टकराव', en: 'Impatience and ego clashes' },
    ],
    kalapurushWhy: [
      { hi: 'कालपुरुष में मेष सिर (मस्तक) का स्वामी - विचार, नेतृत्व और पहली छाप।', en: 'In Kalapurusha Mesha rules the head - thought, leadership and first impression.' },
      { hi: 'सिर की चोट या तनाव पर ध्यान (शास्त्रीय संबंध)।', en: 'Classical link to head injuries or stress - read full chart.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक कुंडली में मेष = लग्न भाव - आत्म, पहचान, शरीर।', en: 'In the natural zodiac Mesha = 1st house - self, identity, body.' },
      { hi: 'धर्मार्थकाम की शुरुआत यहीं से - “मैं कौन हूँ?”', en: 'Life direction begins here - “Who am I?”' },
    ],
    relationships: {
      friendship: { hi: 'सीधा, वफादार, सक्रिय मित्र - साहसिक योजनाएँ पसंद।', en: 'Direct, loyal, active friend - enjoys bold plans.' },
      love: { hi: 'जुनूनी, तेज़ प्रेम - धैर्य की ज़रूरत।', en: 'Passionate, fast romance - needs patience.' },
      conflict: { hi: 'तुरंत प्रतिक्रिया, गुस्सा जल्दी उतरता भी है।', en: 'Quick reaction; anger often fades fast.' },
      leadership: { hi: 'रिश्तों में भी अग्रणी बनना चाहता है।', en: 'Tends to lead even in relationships.' },
    },
    planets: {
      Sun: { hi: 'उच्च (लगभग 10°) - नेतृत्व, आत्मविश्वास, जीवन शक्ति।', en: 'Exalted (~10°) - leadership, confidence, vitality.' },
      Moon: { hi: 'भावुक आवेग, साहसिक भावनाएँ, त्वरित प्रतिक्रिया।', en: 'Emotional impulse, adventurous feelings, quick reactions.' },
      Mars: { hi: 'स्वराशि - अत्यधिक ऊर्जा, साहस, प्रतिस्पर्धा (बल देखें)।', en: 'Own sign - high energy, courage, competition (check strength).' },
      Mercury: { hi: 'तीक्ष्ण वाणी, सीधी बात, त्वरित विचार।', en: 'Sharp speech, direct talk, quick thinking.' },
      Jupiter: { hi: 'धार्मिक साहस, शिक्षा में उत्साह - अति आत्मविश्वास संभव।', en: 'Dhārmic courage, zeal in learning - overconfidence possible.' },
      Venus: { hi: 'जुनूनी प्रेम, सौंदर्य में साहस - आवेग में संबंध।', en: 'Passionate love, bold taste - impulsive bonds.' },
      Saturn: { hi: 'नीच - अनुशासन संघर्ष, विलंब; गहन धैर्य सीख सकता है।', en: 'Debilitated - discipline struggle, delay; can learn deep patience.' },
      Rahu: { hi: 'अत्यंत महत्वाकांक्षा, असामान्य साहस, तेज़ बदलाव।', en: 'Extreme ambition, unconventional boldness, rapid change.' },
      Ketu: { hi: 'आध्यात्मिक योद्धा - वैराग्य या अचानक मोड़।', en: 'Spiritual warrior - detachment or sudden turns.' },
    },
    dignityNotes: [
      { hi: 'सूर्य उच्च - आत्म-शक्ति और नेतृत्व का शिखर।', en: 'Sun exalted - peak of self-power and leadership.' },
      { hi: 'शनि नीच - धैर्य और संरचना की चुनौती; नीच भंग योग देखें।', en: 'Saturn debilitated - patience/structure challenge; check neecha bhanga.' },
      { hi: 'मंगल मूलत्रिकोण क्षेत्र मेष में (0° – 12° परंपरा)।', en: 'Mars moolatrikona zone in Mesha (0° – 12° tradition).' },
    ],
    lifeStages: {
      childhood: { hi: 'सक्रिय, खेल-प्रेमी, प्रतिस्पर्धी।', en: 'Active, playful, competitive child.' },
      career: { hi: 'सैन्य, खेल, इंजीनियरिंग, उद्यमिता, आपात सेवा।', en: 'Military, sports, engineering, entrepreneurship, emergency services.' },
      leadership: { hi: 'कमांडर, कप्तान, संस्थापक।', en: 'Commander, captain, founder.' },
      business: { hi: 'स्टार्टअप, तेज़ विस्तार - धैर्य संतुलन ज़रूरी।', en: 'Startups, rapid expansion - balance with patience.' },
      sports: { hi: 'प्राकृतिक एथलीट - सहनशक्ति और गति।', en: 'Natural athlete - stamina and speed.' },
      spirituality: { hi: 'अग्नि-साधना, हनुमान/कार्तिकेय भक्ति, कर्म योग।', en: 'Agni sadhana, Hanuman/Kartikeya devotion, karma yoga.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'मेष लग्न - पूरे जीवन की दिशा मंगल से; शरीर, साहस और पहचान मंगल-रंगी।', en: 'Mesha lagna - Mars colours the whole life; body, courage and identity.' },
      moon: { hi: 'चंद्र मेष - मन तेज़, भावुक साहस; मूड तुरंत बदल सकता है।', en: 'Moon in Mesha - quick mind, emotional courage; moods shift fast.' },
      sun: { hi: 'सूर्य मेष - मजबूत आत्म, नेतृत्व की पहचान; उच्च हो तो विशेष बल।', en: 'Sun in Mesha - strong self, leader identity; extra power if exalted.' },
    },
    archetypes: [
      { hi: 'योद्धा', en: 'Warrior' },
      { hi: 'अग्रणी', en: 'Pioneer' },
      { hi: 'उद्यमी', en: 'Entrepreneur' },
      { hi: 'खोजकर्ता', en: 'Explorer' },
      { hi: 'सेनापति', en: 'Commander' },
    ],
  },
  2: {
    sanskritMeaning: { hi: 'वृष = बैल; अभी = भयरहित। स्थिर, धैर्यवान शक्ति।', en: 'Vrisha = bull; Abhi = fearless. Steady, patient strength.' },
    introLead: { hi: 'द्वितीय राशि - वसंत की पूर्णता, धन और सुख का संचय। 30° – 60°।', en: 'Second sign - spring’s fullness, accumulation of wealth and comfort. 30° – 60°.' },
    symbolism: [
      { hi: 'बैल खेत जोतता है - परिश्रम, धैर्य, उर्वरता।', en: 'The bull ploughs fields - labour, patience, fertility.' },
      { hi: 'सींग = रक्षा और संसाधन की रक्षा।', en: 'Horns = protection and guarding resources.' },
      { hi: 'स्थिरता और इंद्रिय सुख का प्रतीक।', en: 'Symbol of stability and sensory pleasure.' },
    ],
    mythology: [
      { hi: 'शुक्र-प्रधान - लक्ष्मी, कला और सौंदर्य की ऊर्जा।', en: 'Venus-ruled - energy of Lakshmi, art and beauty.' },
      { hi: 'प्रजापति/वृषभ पुराण में पृथ्वी और संपत्ति से जुड़ाव।', en: 'Linked to earth and prosperity in Puranic tradition.' },
      { hi: 'BPHS: वृषभ धन, वाक् और कुटुम्ब का कारक स्वभाव।', en: 'BPHS: Vrishabha nature carries wealth, speech and family themes.' },
    ],
    elementPositive: [
      { hi: 'स्थिरता और विश्वसनीयता', en: 'Stability and reliability' },
      { hi: 'व्यावहारिक संवेदन-सुख', en: 'Practical enjoyment' },
      { hi: 'धैर्यवान निर्माण', en: 'Patient building' },
    ],
    elementNegative: [
      { hi: 'ज़िद और अड़ियलपन', en: 'Stubbornness' },
      { hi: 'अति संलग्नता सुख में', en: 'Over-attachment to comfort' },
      { hi: 'धीमी परिवर्तन-गति', en: 'Slow to change' },
    ],
    swabhavWhy: { hi: 'स्थिर स्वभाव - धारण, संचय और दीर्घकालिक निर्माण।', en: 'Sthira (fixed) - holds, accumulates and builds long-term.' },
    swabhavExamples: [
      { hi: 'बचत और संपत्ति बनाना', en: 'Saving and building assets' },
      { hi: 'लंबे समय तक एक ही रिश्ता/काम', en: 'Long-term relationship or job' },
      { hi: 'धीरे-धीरे सुधार', en: 'Gradual improvement' },
    ],
    lordRows: [
      { qualityHi: 'सौंदर्य', qualityEn: 'Beauty', exprHi: 'कला और सुख', exprEn: 'Art and pleasure' },
      { qualityHi: 'संयम', qualityEn: 'Steadiness', exprHi: 'विश्वास बनाना', exprEn: 'Builds trust' },
      { qualityHi: 'धन', qualityEn: 'Wealth', exprHi: 'संचय प्रवृत्ति', exprEn: 'Accumulation drive' },
      { qualityHi: 'वाक्', qualityEn: 'Speech', exprHi: 'मधुर या गहरी आवाज़', exprEn: 'Pleasant or rich voice' },
    ],
    appearance: [
      { hi: 'मजबूत गर्दन या कंधे', en: 'Strong neck or shoulders' },
      { hi: 'सुंदर या आकर्षक नेत्र', en: 'Beautiful or attractive eyes' },
      { hi: 'स्थिर, शांत चेहरा', en: 'Calm, steady face' },
    ],
    strengths: [
      { hi: 'विश्वसनीय और धैर्यवान', en: 'Reliable and patient' },
      { hi: 'व्यावहारिक और संसाधन-सज्ज', en: 'Practical and resourceful' },
      { hi: 'कलात्मक संवेदना', en: 'Artistic sensibility' },
    ],
    challenges: [
      { hi: 'अड़ियल और संपत्ति-लालसा', en: 'Stubborn and possessive' },
      { hi: 'परिवर्तन से विरोध', en: 'Resistance to change' },
      { hi: 'आलस्य या सुख-लिप्तता', en: 'Laziness or indulgence' },
    ],
    kalapurushWhy: [
      { hi: 'वृषभ मुख और गला - वाणी, भोजन, संगीत।', en: 'Vrishabha rules face and throat - speech, food, music.' },
      { hi: 'गले की समस्याएँ शास्त्र में देखी जाती हैं।', en: 'Throat issues noted in classical health links.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक कुंडली में 2nd house - धन, वाक्, परिवार।', en: 'Natural chart 2nd house - wealth, speech, family.' },
      { hi: 'अर्थ (साधन) का भाव।', en: 'House of artha (means).' },
    ],
    relationships: {
      friendship: { hi: 'स्थिर, वफादार मित्र - लंबी दोस्ती।', en: 'Steady, loyal friend - long friendships.' },
      love: { hi: 'संगीत, स्पर्श, सुख - गहरा प्रेम धीरे बढ़ता है।', en: 'Touch, comfort, beauty - love grows slowly deep.' },
      conflict: { hi: 'धीरे जुड़ता है क्रोध; छोड़ना मुश्किल।', en: 'Slow to anger; hard to let go.' },
      leadership: { hi: 'शांत प्रभाव, संसाधन से नेतृत्व।', en: 'Quiet influence, leads through resources.' },
    },
    planets: {
      Sun: { hi: 'मध्यम शक्ति - धैर्य से नेतृत्व।', en: 'Moderate power - leadership through patience.' },
      Moon: { hi: 'उच्च - भावनात्मक स्थिरता और सुख-प्रेम।', en: 'Exalted - emotional steadiness and love of comfort.' },
      Mars: { hi: 'तीक्ष्ण ऊर्जा - धैर्य में संघर्ष।', en: 'Sharp energy - friction with patience.' },
      Mercury: { hi: 'व्यावहारिक बुद्धि, वाणी में मधुरता।', en: 'Practical mind, pleasant speech.' },
      Jupiter: { hi: 'विनम्र विस्तार, धन-धर्म संयोजन।', en: 'Modest expansion, wealth-dharma blend.' },
      Venus: { hi: 'स्वराशि - कला, सौंदर्य, समृद्धि की ओर।', en: 'Own sign - art, beauty, toward prosperity.' },
      Saturn: { hi: 'कठोर परिश्रम, धीमी वृद्धि।', en: 'Hard labour, slow growth.' },
      Rahu: { hi: 'भौतिक लालसा, विलासिता में उतार-चढ़ाव।', en: 'Material craving, luxury swings.' },
      Ketu: { hi: 'सुख से वैराग्य या आंतरिक खोज।', en: 'Detachment from comfort or inner search.' },
    },
    dignityNotes: [
      { hi: 'चंद्रमा उच्च वृषभ में - मन की शान्ति और सुख।', en: 'Moon exalted in Taurus - peace of mind and comfort.' },
      { hi: 'शुक्र स्वराशि - सौंदर्य और संबंध बलवान।', en: 'Venus own sign - beauty and bonds strengthened.' },
    ],
    lifeStages: {
      childhood: { hi: 'शांत, संलग्न, खाने-पीने में सुख।', en: 'Calm, attached, enjoys food and touch.' },
      career: { hi: 'वित्त, कृषि, कला, फैशन, भोजन उद्योग।', en: 'Finance, agriculture, arts, fashion, food industry.' },
      leadership: { hi: 'स्थिर प्रबंधक, बैंकर, क्यूरेटर।', en: 'Steady manager, banker, curator.' },
      business: { hi: 'संपत्ति, रियल एस्टेट, लक्ज़री।', en: 'Assets, real estate, luxury goods.' },
      sports: { hi: 'धैर्य खेल - कुश्ती, भारोत्तोलन।', en: 'Endurance sports - wrestling, weightlifting.' },
      spirituality: { hi: 'लक्ष्मी-साधना, प्रकृति भक्ति, मंत्र जप।', en: 'Lakshmi sadhana, nature devotion, mantra japa.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'वृषभ लग्न - शुक्र पूरे जीवन का रंग; शरीर स्थिर, सौंदर्य-प्रेमी।', en: 'Vrishabha lagna - Venus colours life; steady body, loves beauty.' },
      moon: { hi: 'चंद्र वृषभ - शांत मन, सुख-चाह; उच्च हो तो विशेष स्थिरता।', en: 'Moon in Taurus - calm mind, craves comfort; exalted adds stability.' },
      sun: { hi: 'सूर्य वृषभ - धैर्यवान अहंकार, व्यावहारिक लक्ष्य।', en: 'Sun in Taurus - patient ego, practical goals.' },
    },
    archetypes: [
      { hi: 'संचयकर्ता', en: 'Accumulator' },
      { hi: 'कलाकार', en: 'Artist' },
      { hi: 'कृषक', en: 'Cultivator' },
      { hi: 'रक्षक', en: 'Guardian' },
      { hi: 'बैंकर', en: 'Banker' },
    ],
  },
  3: {
    sanskritMeaning: { hi: 'मिथुन = जुड़वाँ - द्वंद्व, संवाद, दोहराव।', en: 'Mithuna = twins - duality, dialogue, repetition.' },
    introLead: { hi: 'तृतीय राशि - वायु तत्त्व, संचार और चतुराई। 60° – 90°।', en: 'Third sign - air element, communication and wit. 60° – 90°.' },
    symbolism: [
      { hi: 'युवा जोड़ा - सीखना, नकल, खेल।', en: 'Young pair - learning, mimicry, play.' },
      { hi: 'हाथ और भुजाएँ - लेखन, व्यापार, कौशल।', en: 'Arms and hands - writing, trade, skill.' },
      { hi: 'दो विचार एक साथ - तर्क और कल्पना।', en: 'Two thoughts at once - logic and imagination.' },
    ],
    mythology: [
      { hi: 'अश्विनीकुमारों की जोड़ी परंपरा से संवाद - चिकित्सा और गति।', en: 'Twin Ashvins tradition - healing and speed.' },
      { hi: 'बुध = विष्णु का अवतार-संदेशवाहक स्वभाव।', en: 'Mercury carries Vishnu’s messenger quality.' },
    ],
    elementPositive: [
      { hi: 'बुद्धि और अनुकूलन', en: 'Intelligence and adaptability' },
      { hi: 'संवाद कौशल', en: 'Communication skill' },
      { hi: 'तेज़ सीखना', en: 'Quick learning' },
    ],
    elementNegative: [
      { hi: 'अस्थिरता और दोहरा स्वभाव', en: 'Restlessness and duality' },
      { hi: 'सतही ज्ञान', en: 'Superficial knowledge' },
      { hi: 'चिंता और तंत्र', en: 'Anxiety and nervousness' },
    ],
    swabhavWhy: { hi: 'द्विस्वभाव - दिन में अलग मूड, अनुकूलन और बदलाव।', en: 'Dwiswabhava - shifts mood, adapts and changes.' },
    swabhavExamples: [
      { hi: 'एक साथ कई काम', en: 'Multitasking' },
      { hi: 'यात्रा और नेटवर्किंग', en: 'Travel and networking' },
      { hi: 'विचार बदलना', en: 'Changing opinions' },
    ],
    lordRows: [
      { qualityHi: 'बुद्धि', qualityEn: 'Intellect', exprHi: 'विश्लेषण', exprEn: 'Analysis' },
      { qualityHi: 'वाणी', qualityEn: 'Speech', exprHi: 'वाक्-चातुर्य', exprEn: 'Verbal wit' },
      { qualityHi: 'व्यापार', qualityEn: 'Trade', exprHi: 'लेखन-व्यवहार', exprEn: 'Writing & deals' },
      { qualityHi: 'युवा ऊर्जा', qualityEn: 'Youthful energy', exprHi: 'जिज्ञासा', exprEn: 'Curiosity' },
    ],
    appearance: [
      { hi: 'स्लिम या चपल शरीर', en: 'Slim or agile body' },
      { hi: 'चंचल नेत्र और हाथ', en: 'Lively eyes and hands' },
      { hi: 'युवा दिखावट', en: 'Youthful look' },
    ],
    strengths: [
      { hi: 'बुद्धिमान और वाक्पटु', en: 'Intelligent and articulate' },
      { hi: 'बहु-कुशल', en: 'Multiskilled' },
      { hi: 'सामाजिक और लचीला', en: 'Social and flexible' },
    ],
    challenges: [
      { hi: 'असंगति और निर्णय में देरी', en: 'Inconsistency and indecision' },
      { hi: 'अति बातचीत', en: 'Excess talk' },
      { hi: 'गहराई की कमी', en: 'Lack of depth' },
    ],
    kalapurushWhy: [
      { hi: 'मिथुन भुजाएँ, कंधे, हाथ - कौशल और संचार।', en: 'Mithuna rules arms, shoulders, hands - skill and communication.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 3rd house - साहस, भाई-बहन, प्रयास।', en: 'Natural 3rd house - courage, siblings, effort.' },
    ],
    relationships: {
      friendship: { hi: 'हँसी-मज़ाक, बातचीत से बंधन।', en: 'Bonds through humour and conversation.' },
      love: { hi: 'मित्रता से प्रेम; बौद्धिक आकर्षण।', en: 'Love from friendship; mental attraction.' },
      conflict: { hi: 'शब्द-युद्ध, शीघ्र क्षमा भी।', en: 'Word battles; may forgive quickly.' },
      leadership: { hi: 'विचारों से प्रभावित करता है।', en: 'Leads through ideas.' },
    },
    planets: {
      Sun: { hi: 'बुद्धिमान अहंकार, लेखन-नेतृत्व।', en: 'Intellectual ego, writing leadership.' },
      Moon: { hi: 'चंचल मन, कल्पनाशील भावनाएँ।', en: 'Restless mind, imaginative feelings.' },
      Mars: { hi: 'तीक्ष्ण बहस, तेज़ हाथ-कौशल।', en: 'Sharp debate, fast hand skills.' },
      Mercury: { hi: 'बलवान बुध - लेखन, व्यापार, विश्लेषण।', en: 'Strong Mercury - writing, trade, analysis.' },
      Jupiter: { hi: 'विविध ज्ञान, कभी अति बात।', en: 'Varied knowledge, sometimes over-talk.' },
      Venus: { hi: 'सौंदर्य में बुद्धि, कला-संवाद।', en: 'Beauty with wit, art-dialogue.' },
      Saturn: { hi: 'गंभीर विचार, धीमी बातचीत।', en: 'Serious thought, slow speech.' },
      Rahu: { hi: 'मीडिया, तकनीक, असामान्य विचार।', en: 'Media, tech, unconventional ideas.' },
      Ketu: { hi: 'मौन या गहन अध्ययन की ओर।', en: 'Toward silence or deep study.' },
    },
    dignityNotes: [{ hi: 'राहु उच्च मिथुन में (परंपरा)।', en: 'Rahu exalted in Gemini (tradition).' }],
    lifeStages: {
      childhood: { hi: 'बातूनी, जिज्ञासु, खेल में चतुर।', en: 'Talkative, curious, clever at play.' },
      career: { hi: 'पत्रकारिता, IT, व्यापार, शिक्षण।', en: 'Journalism, IT, trade, teaching.' },
      leadership: { hi: 'प्रवक्ता, संपादक, कनेक्टर।', en: 'Speaker, editor, connector.' },
      business: { hi: 'ब्रोकरेज, मार्केटिंग, ऐप्स।', en: 'Brokerage, marketing, apps.' },
      sports: { hi: 'तेज़ रिफ्लेक्स - टेनिस, बैडमिंटन।', en: 'Quick reflex - tennis, badminton.' },
      spirituality: { hi: 'मंत्र, जप, ग्रंथ अध्ययन।', en: 'Mantra, japa, scripture study.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'मिथुन लग्न - बुध रंग; शरीर चपल, बुद्धि-प्रधान।', en: 'Mithuna lagna - Mercury colours; agile body, mind-first.' },
      moon: { hi: 'चंद्र मिथुन - भावनाएँ विचारों में बदलतीं।', en: 'Moon in Gemini - feelings shift with thoughts.' },
      sun: { hi: 'सूर्य मिथुन - बौद्धिक पहचान, लेखक-नेता।', en: 'Sun in Gemini - intellectual identity, writer-leader.' },
    },
    archetypes: [
      { hi: 'दूत', en: 'Messenger' },
      { hi: 'लेखक', en: 'Writer' },
      { hi: 'व्यापारी', en: 'Trader' },
      { hi: 'छात्र', en: 'Student' },
      { hi: 'वाक्पटु', en: 'Orator' },
    ],
  },
  4: {
    sanskritMeaning: { hi: 'कर्क = केंकड़ा - कवच, गृह, मातृ-प्रेम।', en: 'Karka = crab - shell, home, mother-love.' },
    introLead: { hi: 'चतुर्थ राशि - जल तत्त्व, चंद्र-स्वामी। 90° – 120°।', en: 'Fourth sign - water element, Moon-ruled. 90° – 120°.' },
    symbolism: [
      { hi: 'केंकड़ा पीछे चलता है - सुरक्षा और स्मृति।', en: 'Crab moves sideways - protection and memory.' },
      { hi: 'कवच = भावनात्मक रक्षा।', en: 'Shell = emotional protection.' },
      { hi: 'जल और चंद्रमा - मन और माता।', en: 'Water and Moon - mind and mother.' },
    ],
    mythology: [
      { hi: 'चंद्र देव - अमृत और मन का स्वामी।', en: 'Chandra - lord of nectar and mind.' },
      { hi: 'माता-परवर्ती परंपरा - पोषण और गृह।', en: 'Mother-centric tradition - nurture and home.' },
    ],
    elementPositive: [
      { hi: 'करुणा और देखभाल', en: 'Compassion and care' },
      { hi: 'स्मृति और लगाव', en: 'Memory and attachment' },
      { hi: 'सहज सहानुभूति', en: 'Natural empathy' },
    ],
    elementNegative: [
      { hi: 'अति संवेदनशीलता', en: 'Oversensitivity' },
      { hi: 'मूड स्विंग', en: 'Mood swings' },
      { hi: 'अतीत में अटकना', en: 'Clinging to the past' },
    ],
    swabhavWhy: { hi: 'चर - भावनात्मक गति, गृह और परिवार में बदलाव।', en: 'Chara - emotional movement, shifts in home and family.' },
    swabhavExamples: [
      { hi: 'घर सजाना या बदलना', en: 'Decorating or moving home' },
      { hi: 'परिवार की देखभाल', en: 'Caring for family' },
      { hi: 'भावनात्मक उतार-चढ़ाव', en: 'Emotional ups and downs' },
    ],
    lordRows: [
      { qualityHi: 'मन', qualityEn: 'Mind', exprHi: 'भावुक गहराई', exprEn: 'Emotional depth' },
      { qualityHi: 'मातृता', qualityEn: 'Nurture', exprHi: 'पोषण', exprEn: 'Nourishing' },
      { qualityHi: 'स्मृति', qualityEn: 'Memory', exprHi: 'परंपरा', exprEn: 'Tradition' },
      { qualityHi: 'कल्पना', qualityEn: 'Imagination', exprHi: 'स्वप्न', exprEn: 'Dreams' },
    ],
    appearance: [
      { hi: 'गोल या नरम विशेषताएँ', en: 'Round or soft features' },
      { hi: 'प्रभावशाली छाती', en: 'Prominent chest' },
      { hi: 'चंद्रमा जैसी चमक (कभी)', en: 'Moon-like glow (sometimes)' },
    ],
    strengths: [
      { hi: 'दयालु और रक्षक', en: 'Kind and protective' },
      { hi: 'सहज और स्मरणशक्ति', en: 'Intuitive with memory' },
      { hi: 'गृह-निर्माता', en: 'Homemaker/builder of home' },
    ],
    challenges: [
      { hi: 'अति लगाव', en: 'Over-attachment' },
      { hi: 'असुरक्षा की भावना', en: 'Insecurity' },
      { hi: 'पुरानी बातें दोहराना', en: 'Holding old hurts' },
    ],
    kalapurushWhy: [
      { hi: 'कर्क वक्ष और हृदय - भावनात्मक केंद्र।', en: 'Karka rules chest and heart - emotional centre.' },
      { hi: 'जल राशि - फेफड़े/स्तन क्षेत्र संवेदनशील।', en: 'Water sign - lungs/chest zone sensitive.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 4th - सुख, माता, गृह।', en: 'Natural 4th - happiness, mother, home.' },
      { hi: 'मोक्ष की नींव - आंतरिक शान्ति।', en: 'Foundation of moksha - inner peace.' },
    ],
    relationships: {
      friendship: { hi: 'गहरा, परिवार जैसा बंधन।', en: 'Deep, family-like bond.' },
      love: { hi: 'सुरक्षा और स्नेह - भावनात्मक घर।', en: 'Safety and affection - emotional home.' },
      conflict: { hi: 'कवच में लौटना, मूक क्रोध।', en: 'Retreats into shell, silent anger.' },
      leadership: { hi: 'मातृवत देखभाल से नेतृत्व।', en: 'Leads through nurturing care.' },
    },
    planets: {
      Sun: { hi: 'आत्मा में कोमलता, गृह-प्रतिष्ठा।', en: 'Softness in self, honour through home.' },
      Moon: { hi: 'स्वराशि - मन बलवान; भावुकता प्रबल।', en: 'Own sign - strong mind; deep feeling.' },
      Mars: { hi: 'रक्षात्मक क्रोध, घर की रक्षा।', en: 'Protective anger, defends home.' },
      Mercury: { hi: 'भावुक लेखन, स्मृति-बुद्धि।', en: 'Emotional writing, memory-intellect.' },
      Jupiter: { hi: 'नीच - आस्था में उतार-चढ़ाव; गुरु कृपा देखें।', en: 'Debilitated - faith swings; check guru grace.' },
      Venus: { hi: 'सौंदर्य और सुख-प्रेम।', en: 'Beauty and love of comfort.' },
      Saturn: { hi: 'भावनात्मक भार, जिम्मेदारी।', en: 'Emotional weight, responsibility.' },
      Rahu: { hi: 'अजीब मूड, विदेश/जल संबंध।', en: 'Odd moods, foreign/water links.' },
      Ketu: { hi: 'आंतरिक वैराग्य, पूर्व जन्म स्मृति।', en: 'Inner detachment, past-life memory.' },
    },
    dignityNotes: [
      { hi: 'गुरु नीच कर्क में - आस्था परीक्षा।', en: 'Jupiter debilitated in Cancer - faith tested.' },
      { hi: 'चंद्र स्वराशि - मन का घर।', en: 'Moon own sign - home of the mind.' },
    ],
    lifeStages: {
      childhood: { hi: 'संवेदनशील, माँ से गहरा बंधन।', en: 'Sensitive, deep bond with mother.' },
      career: { hi: 'देखभाल, खाना, रियल एस्टेट, परामर्श।', en: 'Care, food, real estate, counselling.' },
      leadership: { hi: 'पोषक नेता, समुदाय निर्माता।', en: 'Nurturing leader, community builder.' },
      business: { hi: 'होटल, घरेलू उत्पाद, बच्चों का कार्य।', en: 'Hotels, home products, childcare.' },
      sports: { hi: 'जल खेल, टीम की आत्मा।', en: 'Water sports, team spirit.' },
      spirituality: { hi: 'भक्ति, मातृ-देवी, चंद्र साधना।', en: 'Bhakti, mother-goddess, lunar sadhana.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'कर्क लग्न - चंद्र पूरे जीवन का रंग; शरीर कोमल, गृह-प्रेमी।', en: 'Karka lagna - Moon colours life; soft body, home-loving.' },
      moon: { hi: 'चंद्र कर्क - मन स्थिर और गहरा; स्वराशि बल।', en: 'Moon in Cancer - mind steady and deep; own-sign strength.' },
      sun: { hi: 'सूर्य कर्क - कोमल अहंकार, परिवार-केंद्रित।', en: 'Sun in Cancer - gentle ego, family-centred.' },
    },
    archetypes: [
      { hi: 'माता', en: 'Mother' },
      { hi: 'रक्षक', en: 'Protector' },
      { hi: 'पोषक', en: 'Nurturer' },
      { hi: 'स्मृतिकार', en: 'Keeper of memory' },
      { hi: 'गृहस्थ', en: 'Homemaker' },
    ],
  },
  5: {
    sanskritMeaning: { hi: 'सिंह = शेर - राजसी शक्ति, सूर्य का प्रतीक।', en: 'Simha = lion - regal power, symbol of the Sun.' },
    introLead: { hi: 'पंचम राशि - अग्नि, सूर्य-स्वामी, केंद्र-त्रिकोण ऊर्जा। 120° – 150°।', en: 'Fifth sign - fire, Sun-ruled, kendra-trikona energy. 120° – 150°.' },
    symbolism: [
      { hi: 'शेर - साहस, राज्य, प्रकाश।', en: 'Lion - courage, kingdom, radiance.' },
      { hi: 'सिंहासन - अधिकार और गरिमा।', en: 'Throne - authority and dignity.' },
      { hi: 'हृदय-केंद्र - उदारता और गर्मजोशी।', en: 'Heart-centre - generosity and warmth.' },
    ],
    mythology: [
      { hi: 'सूर्य देव - विश्व की आत्मा, दिन का स्वामी।', en: 'Surya - soul of the world, lord of day.' },
      { hi: 'नृसिंह अवतार - रक्षा और धर्म की तीव्र शक्ति।', en: 'Narasimha avatar - fierce protective dharma.' },
    ],
    elementPositive: [
      { hi: 'उदारता और प्रेरणा', en: 'Generosity and inspiration' },
      { hi: 'नेतृत्व और आत्मविश्वास', en: 'Leadership and confidence' },
      { hi: 'रचनात्मकता', en: 'Creativity' },
    ],
    elementNegative: [
      { hi: 'अहंकार', en: 'Pride' },
      { hi: 'ड्रामा', en: 'Drama' },
      { hi: 'सुनने की कमी', en: 'Poor listening' },
    ],
    swabhavWhy: { hi: 'स्थिर - गरिमा बनाए रखता है, केंद्र में स्थिर प्रकाश।', en: 'Sthira - holds dignity, steady radiance in a kendra.' },
    swabhavExamples: [
      { hi: 'मंच पर रहना पसंद', en: 'Likes being on stage' },
      { hi: 'लंबे समय तक एक विचार पर अड़ना', en: 'Sticking to one vision' },
      { hi: 'शाही व्यवहार', en: 'Regal behaviour' },
    ],
    lordRows: [
      { qualityHi: 'प्रकाश', qualityEn: 'Light', exprHi: 'चमक और दृश्यता', exprEn: 'Shine and visibility' },
      { qualityHi: 'अधिकार', qualityEn: 'Authority', exprHi: 'नेतृत्व', exprEn: 'Command' },
      { qualityHi: 'उदारता', qualityEn: 'Generosity', exprHi: 'दान और गर्मजोशी', exprEn: 'Giving and warmth' },
      { qualityHi: 'गरिमा', qualityEn: 'Dignity', exprHi: 'सम्मान', exprEn: 'Honour' },
    ],
    appearance: [
      { hi: 'मान्य उपस्थिति, सीधी रीढ़', en: 'Commanding presence, straight spine' },
      { hi: 'घने या सुनहरे बाल', en: 'Thick or golden hair' },
      { hi: 'चमकती आँखें', en: 'Bright eyes' },
    ],
    strengths: [
      { hi: 'महान हृदय और साहस', en: 'Big heart and courage' },
      { hi: 'रचनात्मक नेता', en: 'Creative leader' },
      { hi: 'वफादार', en: 'Loyal' },
    ],
    challenges: [
      { hi: 'अहंकार और ड्रामा', en: 'Pride and drama' },
      { hi: 'आलोचना सहन न करना', en: 'Cannot bear criticism' },
      { hi: 'ध्यान का केंद्र बनना', en: 'Need to be centre of attention' },
    ],
    kalapurushWhy: [
      { hi: 'सिंह ऊपरी उदर - पाचन, यकृत, पित्त।', en: 'Simha rules upper abdomen - digestion, liver, bile.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 5th - पुत्र, बुद्धि, पूर्व-पुण्य।', en: 'Natural 5th - children, intelligence, past merit.' },
    ],
    relationships: {
      friendship: { hi: 'उदार मित्र, वफादार - सम्मान चाहिए।', en: 'Generous loyal friend - wants respect.' },
      love: { hi: 'रोमांटिक, शाही प्रेम - प्रशंसा ज़रूरी।', en: 'Romantic, royal love - needs praise.' },
      conflict: { hi: 'गरिमा टूटे तो तीव्र क्रोध।', en: 'Fierce anger if dignity hurt.' },
      leadership: { hi: 'प्राकृतिक कप्तान, रक्षक।', en: 'Natural captain and protector.' },
    },
    planets: {
      Sun: { hi: 'स्वराशि - शक्तिशाली आत्म, नेतृत्व।', en: 'Own sign - powerful self, leadership.' },
      Moon: { hi: 'गर्वीली भावनाएँ, रचनात्मक मन।', en: 'Proud feelings, creative mind.' },
      Mars: { hi: 'योद्धा-नेता, साहसिक कार्य।', en: 'Warrior-leader, bold action.' },
      Mercury: { hi: 'वाक् में प्रभाव, रचनात्मक लेखन।', en: 'Commanding speech, creative writing.' },
      Jupiter: { hi: 'धर्म और शिक्षा में गरिमा।', en: 'Dignity in dharma and teaching.' },
      Venus: { hi: 'कला, रंगमंच, प्रेम में चमक।', en: 'Shine in art, stage, romance.' },
      Saturn: { hi: 'गरिमा पर बोझ, धीमा प्रकाश।', en: 'Weight on pride, slow radiance.' },
      Rahu: { hi: 'असामान्य प्रसिद्धि, राजनीति।', en: 'Unusual fame, politics.' },
      Ketu: { hi: 'आंतरिक वैराग्य अहंकार से।', en: 'Detachment from ego.' },
    },
    dignityNotes: [{ hi: 'सूर्य स्वराशि - आत्मा का सिंहासन।', en: 'Sun own sign - throne of the soul.' }],
    lifeStages: {
      childhood: { hi: 'चंचल, केंद्र में रहना चाहता है।', en: 'Playful, wants centre stage.' },
      career: { hi: 'सरकार, कला, मनोरंजन, प्रबंधन।', en: 'Government, arts, entertainment, management.' },
      leadership: { hi: 'राजा, निर्देशक, संस्थापक।', en: 'King, director, founder.' },
      business: { hi: 'ब्रांड, लक्ज़री, क्रिएटिव एजेंसी।', en: 'Brands, luxury, creative agency.' },
      sports: { hi: 'शक्ति और प्रदर्शन खेल।', en: 'Power and performance sports.' },
      spirituality: { hi: 'सूर्य उपासना, सूर्य नमस्कार।', en: 'Surya worship, Surya Namaskar.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'सिंह लग्न - सूर्य रंग; शरीर गरिमामय, नेता स्वभाव।', en: 'Simha lagna - Sun colours; regal body, leader nature.' },
      moon: { hi: 'चंद्र सिंह - गर्वीला मन, भावनात्मक नाटक।', en: 'Moon in Leo - proud mind, emotional drama.' },
      sun: { hi: 'सूर्य सिंह - शक्तिशाली पहचान; स्वराशि बल।', en: 'Sun in Leo - powerful identity; own-sign strength.' },
    },
    archetypes: [
      { hi: 'राजा', en: 'King' },
      { hi: 'कलाकार', en: 'Performer' },
      { hi: 'नेता', en: 'Leader' },
      { hi: 'रक्षक', en: 'Protector' },
      { hi: 'दानी', en: 'Patron' },
    ],
  },
  6: {
    sanskritMeaning: { hi: 'कन्या = कुँवारी कन्या - शुद्धि, सेवा, विश्लेषण।', en: 'Kanya = maiden - purity, service, analysis.' },
    introLead: { hi: 'षष्ठ राशि - पृथ्वी, बुध-स्वामी, द्विस्वभाव। 150° – 180°।', en: 'Sixth sign - earth, Mercury-ruled, dual. 150° – 180°.' },
    symbolism: [
      { hi: 'कन्या हाथ में अनाज - छँटाई, शुद्धि।', en: 'Girl with grain - sorting, purification.' },
      { hi: 'विवरण और क्रम - व्यवस्था।', en: 'Detail and order - organisation.' },
      { hi: 'सेवा और विनम्रता।', en: 'Service and humility.' },
    ],
    mythology: [
      { hi: 'दुर्गा/विष्णु की कन्या शक्ति - शुद्धि से विजय।', en: 'Kanya shakti of Durga/Vishnu - victory through purity.' },
      { hi: 'बुध = विवेक और गणना।', en: 'Mercury = discrimination and calculation.' },
    ],
    elementPositive: [
      { hi: 'विश्लेषण और सटीकता', en: 'Analysis and precision' },
      { hi: 'सेवा-भाव', en: 'Spirit of service' },
      { hi: 'व्यावहारिक बुद्धि', en: 'Practical intelligence' },
    ],
    elementNegative: [
      { hi: 'अति आलोचना', en: 'Over-criticism' },
      { hi: 'चिंता और तनाव', en: 'Worry and stress' },
      { hi: 'पूर्णतावाद', en: 'Perfectionism' },
    ],
    swabhavWhy: { hi: 'द्विस्वभाव - काम में गहराई, फिर अनुकूलन।', en: 'Dwiswabhava - depth in work, then adaptation.' },
    swabhavExamples: [
      { hi: 'सूची और अनुसूची बनाना', en: 'Making lists and schedules' },
      { hi: 'स्वास्थ्य दिनचर्या', en: 'Health routines' },
      { hi: 'विवरण में खो जाना', en: 'Lost in details' },
    ],
    lordRows: [
      { qualityHi: 'विवेक', qualityEn: 'Discrimination', exprHi: 'छँटाई', exprEn: 'Sorting' },
      { qualityHi: 'गणना', qualityEn: 'Calculation', exprHi: 'लेखा-विज्ञान', exprEn: 'Accounts & science' },
      { qualityHi: 'सेवा', qualityEn: 'Service', exprHi: 'उपचार', exprEn: 'Healing work' },
      { qualityHi: 'विनम्रता', qualityEn: 'Humility', exprHi: 'कम बोलना', exprEn: 'Understated speech' },
    ],
    appearance: [
      { hi: 'साफ-सुथरा, सुडौल', en: 'Neat, trim' },
      { hi: 'तीक्ष्ण नज़र', en: 'Sharp gaze' },
      { hi: 'युवा दिखावट लंबे समय', en: 'Youthful look for long' },
    ],
    strengths: [
      { hi: 'विश्लेषक और व्यवस्थित', en: 'Analytical and organised' },
      { hi: 'सेवा-प्रधान', en: 'Service-oriented' },
      { hi: 'विनम्र बुद्धि', en: 'Humble intelligence' },
    ],
    challenges: [
      { hi: 'अति आलोचनात्मक', en: 'Over-critical' },
      { hi: 'चिंताग्रस्त', en: 'Anxious' },
      { hi: 'आत्म-संदेह', en: 'Self-doubt' },
    ],
    kalapurushWhy: [
      { hi: 'कन्या मध्य उदर - आंत, पाचन, अग्न्याशय।', en: 'Kanya rules mid abdomen - intestines, digestion, pancreas.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 6th - शत्रु, रोग, सेवा।', en: 'Natural 6th - enemies, disease, service.' },
    ],
    relationships: {
      friendship: { hi: 'वफादार सहायक, व्यावहारिक सलाह।', en: 'Loyal helper, practical advice.' },
      love: { hi: 'विनम्र प्रेम, सेवा से प्यार।', en: 'Modest love, affection through service.' },
      conflict: { hi: 'आलोचना से घाव; माफ़ी धीरे।', en: 'Hurt by criticism; slow to forgive.' },
      leadership: { hi: 'विवरण-प्रबंधक, प्रशासक।', en: 'Detail-manager, administrator.' },
    },
    planets: {
      Sun: { hi: 'विनम्र अहंकार, सेवा-नेतृत्व।', en: 'Humble ego, service leadership.' },
      Moon: { hi: 'चिंताग्रस्त मन, स्वास्थ्य-चेतना।', en: 'Worried mind, health awareness.' },
      Mars: { hi: 'सर्जिकल ऊर्जा, तीव्र काम।', en: 'Surgical energy, sharp work.' },
      Mercury: { hi: 'बलवान विश्लेषण, लेखा, चिकित्सा।', en: 'Strong analysis, accounts, medicine.' },
      Jupiter: { hi: 'विनम्र ज्ञान, शिक्षा-सेवा।', en: 'Modest wisdom, teaching-service.' },
      Venus: { hi: 'सौंदर्य में शुद्धि, सादगी।', en: 'Purity in beauty, simplicity.' },
      Saturn: { hi: 'कठोर अनुशासन, धीमी सेवा।', en: 'Hard discipline, slow service.' },
      Rahu: { hi: 'तकनीकी सेवा, असामान्य स्वास्थ्य ध्यान।', en: 'Tech service, unusual health focus.' },
      Ketu: { hi: 'आंतरिक शुद्धि, वैराग्य सेवा।', en: 'Inner purification, detached service.' },
    },
    dignityNotes: [
      { hi: 'बुध उच्च कन्या में - विश्लेषण शिखर।', en: 'Mercury exalted in Virgo - peak analysis.' },
      { hi: 'शुक्र नीच कन्या में - सुख में संघर्ष।', en: 'Venus debilitated in Virgo - comfort struggle.' },
    ],
    lifeStages: {
      childhood: { hi: 'शांत, पढ़ाई-प्रेमी, सहायक।', en: 'Quiet, studious, helpful child.' },
      career: { hi: 'चिकित्सा, लेखा, संपादन, QA, शोध।', en: 'Medicine, accounting, editing, QA, research.' },
      leadership: { hi: 'प्रशासक, चिकित्सक-प्रमुख।', en: 'Administrator, chief physician.' },
      business: { hi: 'हेल्थकेयर, सॉफ्टवेयर, सलाह।', en: 'Healthcare, software, consulting.' },
      sports: { hi: 'सहनशक्ति, तकनीकी खेल।', en: 'Endurance, technical sports.' },
      spirituality: { hi: 'सेवा योग, शुद्धि व्रत।', en: 'Karma yoga, purification vows.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'कन्या लग्न - बुध रंग; शरीर साफ, विश्लेषक।', en: 'Kanya lagna - Mercury colours; neat body, analytical.' },
      moon: { hi: 'चंद्र कन्या - चिंताग्रस्त मन, सेवा-भाव।', en: 'Moon in Virgo - worried mind, service bent.' },
      sun: { hi: 'सूर्य कन्या - विनम्र पहचान, कर्म-केंद्रित।', en: 'Sun in Virgo - humble identity, work-centred.' },
    },
    archetypes: [
      { hi: 'चिकित्सक', en: 'Healer' },
      { hi: 'विश्लेषक', en: 'Analyst' },
      { hi: 'लेखाकार', en: 'Accountant' },
      { hi: 'संपादक', en: 'Editor' },
      { hi: 'सेवक', en: 'Servant' },
    ],
  },
  7: {
    sanskritMeaning: { hi: 'तुला = तराजू - संतुलन, न्याय, सौंदर्य।', en: 'Tula = scales - balance, justice, beauty.' },
    introLead: { hi: 'सप्तम राशि - वायु, शुक्र-स्वामी, विवाह-कारक। 180° – 210°।', en: 'Seventh sign - air, Venus-ruled, marriage significator. 180° – 210°.' },
    symbolism: [
      { hi: 'तराजू - न्याय, समझौता, संबंध।', en: 'Scales - justice, compromise, relationship.' },
      { hi: 'पश्चिम दिशा - सूर्यास्त, साझेदारी।', en: 'West direction - sunset, partnership.' },
      { hi: 'कला और सामंजस्य।', en: 'Art and harmony.' },
    ],
    mythology: [
      { hi: 'शुक्र = दैत्यगुरु भी - कूटनीति और सौंदर्य शास्त्र।', en: 'Venus as daityaguru - diplomacy and beauty science.' },
      { hi: 'यम और न्याय की परंपरा से जुड़ाव।', en: 'Linked to Yama and justice traditions.' },
    ],
    elementPositive: [
      { hi: 'सामंजस्य और कूटनीति', en: 'Harmony and diplomacy' },
      { hi: 'सौंदर्य-संवेदना', en: 'Aesthetic sense' },
      { hi: 'न्याय की भावना', en: 'Sense of fairness' },
    ],
    elementNegative: [
      { hi: 'निर्णय में देरी', en: 'Indecision' },
      { hi: 'दूसरों की राय पर निर्भरता', en: 'Dependence on others’ views' },
      { hi: 'सतही सुख', en: 'Superficial pleasure' },
    ],
    swabhavWhy: { hi: 'चर - संबंधों में गति, साझेदारी बदलती रहती है।', en: 'Chara - movement in relationships, shifting partnerships.' },
    swabhavExamples: [
      { hi: 'मध्यस्थता करना', en: 'Mediating disputes' },
      { hi: 'सौंदर्य और फैशन में रुचि', en: 'Interest in beauty and fashion' },
      { hi: 'साझेदारी बदलना', en: 'Changing partners (business/life)' },
    ],
    lordRows: [
      { qualityHi: 'सौंदर्य', qualityEn: 'Beauty', exprHi: 'कला-संतुलन', exprEn: 'Art-balance' },
      { qualityHi: 'कूटनीति', qualityEn: 'Diplomacy', exprHi: 'समझौता', exprEn: 'Compromise' },
      { qualityHi: 'प्रेम', qualityEn: 'Love', exprHi: 'साझेदारी', exprEn: 'Partnership' },
      { qualityHi: 'विलास', qualityEn: 'Refinement', exprHi: 'शिष्टाचार', exprEn: 'Grace' },
    ],
    appearance: [
      { hi: 'आकर्षक, संतुलित विशेषताएँ', en: 'Attractive, balanced features' },
      { hi: 'मधुर मुस्कान', en: 'Pleasant smile' },
      { hi: 'सुडौल शरीर', en: 'Graceful body' },
    ],
    strengths: [
      { hi: 'न्यायप्रिय और कूटनैतिक', en: 'Fair and diplomatic' },
      { hi: 'कलात्मक', en: 'Artistic' },
      { hi: 'सामाजिक सामंजस्य', en: 'Social harmony' },
    ],
    challenges: [
      { hi: 'अनिर्णय', en: 'Indecision' },
      { hi: 'संघर्ष से बचना', en: 'Avoiding conflict' },
      { hi: 'दूसरों को खुश रखना', en: 'People-pleasing' },
    ],
    kalapurushWhy: [
      { hi: 'तुला निचला उदर - वृक्क, प्रजनन संतुलन।', en: 'Tula rules lower abdomen - kidneys, reproductive balance.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 7th - विवाह, साझेदारी, व्यापार।', en: 'Natural 7th - marriage, partnership, trade.' },
    ],
    relationships: {
      friendship: { hi: 'विनम्र, संतुलित मित्र; सौंदर्य साझा।', en: 'Polite balanced friend; shares beauty.' },
      love: { hi: 'रोमांटिक, समानता चाहिए; विवाह-प्रधान।', en: 'Romantic, wants equality; marriage-focused.' },
      conflict: { hi: 'टालना या वकील जैसी बहस।', en: 'Avoids or argues like a lawyer.' },
      leadership: { hi: 'सहमति से नेतृत्व, मध्यस्थ।', en: 'Leads by consensus, mediator.' },
    },
    planets: {
      Sun: { hi: 'साझेदारी में पहचान; कभी अहंकार संघर्ष।', en: 'Identity in partnership; ego clashes possible.' },
      Moon: { hi: 'सामंजस्य की भावना; मूड साझेदार से जुड़ा।', en: 'Need for harmony; mood tied to partner.' },
      Mars: { hi: 'नीच - क्रोध दबा; संबंध संघर्ष।', en: 'Debilitated - suppressed anger; relationship friction.' },
      Mercury: { hi: 'वाक्-कूटनीति, अनुबंध-लेखन।', en: 'Diplomatic speech, contract writing.' },
      Jupiter: { hi: 'धर्म और न्याय में विस्तार।', en: 'Expansion in dharma and justice.' },
      Venus: { hi: 'स्वराशि - प्रेम, कला, विवाह शिखर।', en: 'Own sign - peak love, art, marriage.' },
      Saturn: { hi: 'उच्च - गंभीर संबंध, दीर्घकालिक विवाह।', en: 'Exalted - serious bonds, long marriage.' },
      Rahu: { hi: 'असामान्य साझेदारी, विदेशी संबंध।', en: 'Unusual partnership, foreign ties.' },
      Ketu: { hi: 'संबंध से वैराग्य या आध्यात्मिक जोड़।', en: 'Detachment from bonds or spiritual union.' },
    },
    dignityNotes: [
      { hi: 'शनि उच्च तुला - अनुशासित न्याय।', en: 'Saturn exalted in Libra - disciplined justice.' },
      { hi: 'मंगल नीच - क्रोध संघर्ष।', en: 'Mars debilitated - anger struggles.' },
    ],
    lifeStages: {
      childhood: { hi: 'विनम्र, सौंदर्य-प्रेमी, मध्यस्थ।', en: 'Polite, beauty-loving, peacemaker child.' },
      career: { hi: 'कानून, फैशन, कूटनीति, डिज़ाइन।', en: 'Law, fashion, diplomacy, design.' },
      leadership: { hi: 'न्यायाधीश, राजदूत, क्यूरेटर।', en: 'Judge, ambassador, curator.' },
      business: { hi: 'लक्ज़री, विवाह उद्योग, कला व्यापार।', en: 'Luxury, wedding industry, art trade.' },
      sports: { hi: 'संतुलन खेल - फेंसिंग, डांस।', en: 'Balance sports - fencing, dance.' },
      spirituality: { hi: 'लक्ष्मी-नारायण, भक्ति-सौंदर्य।', en: 'Lakshmi-Narayana, devotional beauty.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'तुला लग्न - शुक्र रंग; शरीर आकर्षक, संबंध-केंद्रित।', en: 'Tula lagna - Venus colours; attractive body, relationship-centred.' },
      moon: { hi: 'चंद्र तुला - मन को सामंजस्य चाहिए।', en: 'Moon in Libra - mind craves harmony.' },
      sun: { hi: 'सूर्य तुला - पहचान साझेदारी से; न्याय-प्रेमी।', en: 'Sun in Libra - identity through partnership; fair-minded.' },
    },
    archetypes: [
      { hi: 'न्यायाधीश', en: 'Judge' },
      { hi: 'कलाकार', en: 'Artist' },
      { hi: 'राजदूत', en: 'Diplomat' },
      { hi: 'डिज़ाइनर', en: 'Designer' },
      { hi: 'मध्यस्थ', en: 'Mediator' },
    ],
  },
  8: {
    sanskritMeaning: { hi: 'वृश्चिक = बिच्छू - गहराई, रहस्य, परिवर्तन।', en: 'Vrishchika = scorpion - depth, mystery, transformation.' },
    introLead: { hi: 'अष्टम राशि - जल (कुछ शास्त्रों में अग्नि भी), मंगल-स्वामी। 210° – 240°।', en: 'Eighth sign - water (some texts also fire), Mars-ruled. 210° – 240°.' },
    symbolism: [
      { hi: 'बिच्छू की डंक - छिपी शक्ति, प्रतिशोध।', en: 'Scorpion sting - hidden power, retaliation.' },
      { hi: 'पुनर्जन्म और रहस्य - अष्टम भाव की ऊर्जा।', en: 'Rebirth and secrets - 8th-house energy.' },
      { hi: 'गहरा जल - अंतर्मन।', en: 'Deep water - inner psyche.' },
    ],
    mythology: [
      { hi: 'मंगल की रात्रि-शैली - रणनीति बनाम मेष की सीधी लड़ाई।', en: 'Mars’s nocturnal style - strategy vs Mesha’s direct fight.' },
      { hi: 'शिव-तांडव और विनाश-पुनर्जन्म चक्र।', en: 'Shiva’s tandava - destruction-rebirth cycle.' },
    ],
    elementPositive: [
      { hi: 'गहन अंतर्दृष्टि', en: 'Deep insight' },
      { hi: 'सहनशक्ति', en: 'Endurance' },
      { hi: 'परिवर्तन की शक्ति', en: 'Power to transform' },
    ],
    elementNegative: [
      { hi: 'संदेह और ज़हर-जैसी वाणी', en: 'Suspicion and stinging speech' },
      { hi: 'बदला', en: 'Vengefulness' },
      { hi: 'छिपाना', en: 'Secrecy' },
    ],
    swabhavWhy: { hi: 'स्थिर - भावनाएँ गहराई में धंसती हैं, मुश्किल से बदलती।', en: 'Sthira - feelings sink deep, hard to change.' },
    swabhavExamples: [
      { hi: 'रहस्य रखना', en: 'Keeping secrets' },
      { hi: 'गहन शोध', en: 'Deep research' },
      { hi: 'लंबे समय तक बदला याद रखना', en: 'Long memory for slights' },
    ],
    lordRows: [
      { qualityHi: 'रणनीति', qualityEn: 'Strategy', exprHi: 'गुप्त योजना', exprEn: 'Hidden plans' },
      { qualityHi: 'साहस', qualityEn: 'Courage', exprHi: 'संकट में टिकना', exprEn: 'Stands in crisis' },
      { qualityHi: 'तीव्रता', qualityEn: 'Intensity', exprHi: 'पूर्ण समर्पण', exprEn: 'Total commitment' },
      { qualityHi: 'परिवर्तन', qualityEn: 'Transformation', exprHi: 'पुनर्जन्म', exprEn: 'Rebirth' },
    ],
    appearance: [
      { hi: 'तीक्ष्ण या गहरी आँखें', en: 'Piercing or deep eyes' },
      { hi: 'गुप्त, शक्तिशाली उपस्थिति', en: 'Magnetic, powerful presence' },
      { hi: 'कभी छोटा कद', en: 'Sometimes compact stature' },
    ],
    strengths: [
      { hi: 'गहन और वफादार', en: 'Deep and loyal' },
      { hi: 'संकट में शक्ति', en: 'Strong in crisis' },
      { hi: 'अनुसंधान क्षमता', en: 'Research ability' },
    ],
    challenges: [
      { hi: 'संदेह और ईर्ष्या', en: 'Suspicion and jealousy' },
      { hi: 'बदला लेना', en: 'Holding grudges' },
      { hi: 'अति गुप्तता', en: 'Excess secrecy' },
    ],
    kalapurushWhy: [
      { hi: 'वृश्चिक श्रोणि और गुप्त प्रणाली - कालपुरुष का संवेदनशील क्षेत्र।', en: 'Vrishchika rules pelvis and private systems - sensitive Kalapurusha zone.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 8th - आयु, रहस्य, विरासत।', en: 'Natural 8th - longevity, secrets, inheritance.' },
    ],
    relationships: {
      friendship: { hi: 'कम पर गहरे मित्र; आजीवन वफादारी।', en: 'Few but deep friends; lifelong loyalty.' },
      love: { hi: 'तीव्र, समर्पित प्रेम - विश्वास ज़रूरी।', en: 'Intense devoted love - trust essential.' },
      conflict: { hi: 'डंक मार सकता है; माफ़ी धीरे।', en: 'Can sting; slow to forgive.' },
      leadership: { hi: 'संकट-नेता, रणनीतिकार।', en: 'Crisis leader, strategist.' },
    },
    planets: {
      Sun: { hi: 'गुप्त अहंकार, शक्ति-परिवर्तन।', en: 'Hidden ego, power-transformation.' },
      Moon: { hi: 'नीच - भावनात्मक उथल-पुथल; गहरा मन।', en: 'Debilitated - emotional turmoil; deep mind.' },
      Mars: { hi: 'स्वराशि - रणनीतिक साहस, शोध।', en: 'Own sign - strategic courage, research.' },
      Mercury: { hi: 'तीक्ष्ण जांच, गुप्त जानकारी।', en: 'Sharp inquiry, secret information.' },
      Jupiter: { hi: 'गहन धर्म, तंत्र-ज्ञान रुचि।', en: 'Deep dharma, interest in esoteric wisdom.' },
      Venus: { hi: 'तीव्र प्रेम, जुनून।', en: 'Intense passion in love.' },
      Saturn: { hi: 'कर्मिक परीक्षा, धीर सहनशक्ति।', en: 'Karmic tests, slow endurance.' },
      Rahu: { hi: 'असामान्य रहस्य, विदेशी तंत्र।', en: 'Unusual secrets, foreign occult.' },
      Ketu: { hi: 'उच्च - आध्यात्मिक वैराग्य, मोक्ष झुकाव।', en: 'Exalted - spiritual detachment, moksha bent.' },
    },
    dignityNotes: [
      { hi: 'चंद्र नीच, केतु उच्च वृश्चिक में।', en: 'Moon debilitated, Ketu exalted in Scorpio.' },
      { hi: 'मंगल स्वराशि - रणनीतिक शक्ति।', en: 'Mars own sign - strategic power.' },
    ],
    lifeStages: {
      childhood: { hi: 'गुप्त, तीव्र भावनाएँ।', en: 'Private, intense feelings.' },
      career: { hi: 'शोध, सर्जरी, जासूसी, मनोविज्ञान, वित्त।', en: 'Research, surgery, detective work, psychology, finance.' },
      leadership: { hi: 'संकट-प्रबंधक, रणनीतिकार।', en: 'Crisis manager, strategist.' },
      business: { hi: 'बीमा, विरासत, खनन, तंत्र।', en: 'Insurance, inheritance, mining, occult fields.' },
      sports: { hi: 'संघर्ष खेल - मार्शल आर्ट्स।', en: 'Combat sports - martial arts.' },
      spirituality: { hi: 'तंत्र, शिव-साधना, गहन ध्यान।', en: 'Tantra, Shiva sadhana, deep meditation.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'वृश्चिक लग्न - मंगल रंग; शरीर चुंबकीय, गुप्त।', en: 'Vrishchika lagna - Mars colours; magnetic, secretive body.' },
      moon: { hi: 'चंद्र वृश्चिक - गहरा मन; नीच हो तो भावनात्मक तूफान।', en: 'Moon in Scorpio - deep mind; debilitated adds emotional storms.' },
      sun: { hi: 'सूर्य वृश्चिक - शक्ति-पहचान, परिवर्तनकारी।', en: 'Sun in Scorpio - power-identity, transformative.' },
    },
    archetypes: [
      { hi: 'जासूस', en: 'Detective' },
      { hi: 'सर्जन', en: 'Surgeon' },
      { hi: 'योगी', en: 'Mystic' },
      { hi: 'रणनीतिकार', en: 'Strategist' },
      { hi: 'पुनर्जीवित', en: 'Phoenix' },
    ],
  },
  9: {
    sanskritMeaning: { hi: 'धनु = धनुष - लक्ष्य, धर्म, दूर दृष्टि।', en: 'Dhanu = bow - aim, dharma, far vision.' },
    introLead: { hi: 'नवम राशि - अग्नि, गुरु-स्वामी, भाग्य-भाव। 240° – 270°।', en: 'Ninth sign - fire, Jupiter-ruled, fortune house. 240° – 270°.' },
    symbolism: [
      { hi: 'धनुर्धर - तीर से लक्ष्य।', en: 'Archer - arrow toward target.' },
      { hi: 'अश्व और यात्रा - तीर्थ, विदेश।', en: 'Horse and travel - pilgrimage, abroad.' },
      { hi: 'गुरु और धर्म - उच्च शिक्षा।', en: 'Guru and dharma - higher learning.' },
    ],
    mythology: [
      { hi: 'गुरु बृहस्पति - देवगुरु, धर्म शिक्षक।', en: 'Guru Brihaspati - devaguru, teacher of dharma.' },
      { hi: 'राम-लक्ष्मण धनुष परंपरा।', en: 'Rama-Lakshmana bow tradition.' },
    ],
    elementPositive: [
      { hi: 'आशावाद और धर्म', en: 'Optimism and dharma' },
      { hi: 'उच्च ज्ञान की खोज', en: 'Quest for higher knowledge' },
      { hi: 'उदार विस्तार', en: 'Generous expansion' },
    ],
    elementNegative: [
      { hi: 'अति आत्मविश्वास', en: 'Overconfidence' },
      { hi: 'सिद्धांतवाद', en: 'Dogmatism' },
      { hi: 'बेचैनी', en: 'Restlessness' },
    ],
    swabhavWhy: { hi: 'द्विस्वभाव - धर्म और व्यवहार में अनुकूलन।', en: 'Dwiswabhava - adapts between dharma and practical life.' },
    swabhavExamples: [
      { hi: 'यात्रा और तीर्थ', en: 'Travel and pilgrimage' },
      { hi: 'शिक्षा बदलना', en: 'Changing fields of study' },
      { hi: 'दर्शन चर्चा', en: 'Philosophy debates' },
    ],
    lordRows: [
      { qualityHi: 'ज्ञान', qualityEn: 'Wisdom', exprHi: 'शिक्षा', exprEn: 'Teaching' },
      { qualityHi: 'भाग्य', qualityEn: 'Fortune', exprHi: 'अवसर', exprEn: 'Opportunity' },
      { qualityHi: 'धर्म', qualityEn: 'Dharma', exprHi: 'नीति', exprEn: 'Ethics' },
      { qualityHi: 'विस्तार', qualityEn: 'Expansion', exprHi: 'यात्रा', exprEn: 'Travel' },
    ],
    appearance: [
      { hi: 'लंबा या स्थूल शरीर', en: 'Tall or sturdy body' },
      { hi: 'खुली मुस्कान', en: 'Open smile' },
      { hi: 'माथा प्रभावशाली', en: 'Prominent forehead' },
    ],
    strengths: [
      { hi: 'आशावादी और ईमानदार', en: 'Optimistic and honest' },
      { hi: 'शिक्षक स्वभाव', en: 'Teacher nature' },
      { hi: 'साहसिक यात्री', en: 'Adventurous traveller' },
    ],
    challenges: [
      { hi: 'अति वाद-विवाद', en: 'Excess argument' },
      { hi: 'वादानुकूलता', en: 'Preaching' },
      { hi: 'वचन से आगे बढ़ना', en: 'Overpromising' },
    ],
    kalapurushWhy: [
      { hi: 'धनु जंघा और कूल्हे - चलने-दौड़ने की शक्ति।', en: 'Dhanu rules thighs and hips - power to walk and run.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 9th - धर्म, गुरु, भाग्य।', en: 'Natural 9th - dharma, guru, fortune.' },
    ],
    relationships: {
      friendship: { hi: 'उदार, हँसी-मज़ाक, दर्शन साझा।', en: 'Generous, humorous, shares philosophy.' },
      love: { hi: 'स्वतंत्रता चाहिए; साथ यात्रा।', en: 'Needs freedom; travel together.' },
      conflict: { hi: 'सिद्धांत पर लड़ाई; जल्दी भूल जाता है।', en: 'Fights on principles; forgives fast.' },
      leadership: { hi: 'गुरु-नेता, प्रेरक वक्ता।', en: 'Guru-leader, inspiring speaker.' },
    },
    planets: {
      Sun: { hi: 'धर्म-नेता, पिता-सम्मान।', en: 'Dharma-leader, father honour.' },
      Moon: { hi: 'आशावादी मन, यात्रा-प्रेम।', en: 'Optimistic mind, loves travel.' },
      Mars: { hi: 'धर्म-योद्धा, साहसिक तीर्थ।', en: 'Dharma warrior, bold pilgrimage.' },
      Mercury: { hi: 'दर्शन-लेखन, भाषा-शिक्षा।', en: 'Philosophy writing, language teaching.' },
      Jupiter: { hi: 'स्वराशि - गुरु बल, भाग्य, ज्ञान।', en: 'Own sign - guru strength, fortune, wisdom.' },
      Venus: { hi: 'कला-धर्म, विदेशी सौंदर्य।', en: 'Art-dharma, foreign beauty.' },
      Saturn: { hi: 'गंभीर धर्म, धीमी यात्रा।', en: 'Serious dharma, slow travel.' },
      Rahu: { hi: 'विदेशी धर्म, असामान्य गुरु।', en: 'Foreign religion, unusual guru.' },
      Ketu: { hi: 'आंतरिक मोक्ष-खोज।', en: 'Inner moksha search.' },
    },
    dignityNotes: [{ hi: 'गुरु स्वराशि - भाग्य और ज्ञान का घर।', en: 'Jupiter own sign - home of fortune and wisdom.' }],
    lifeStages: {
      childhood: { hi: 'जिज्ञासु, प्रश्न करने वाला।', en: 'Curious, question-asking child.' },
      career: { hi: 'शिक्षा, कानून, धर्म, पर्यटन, प्रकाशन।', en: 'Education, law, religion, tourism, publishing.' },
      leadership: { hi: 'गुरु, प्रोफेसर, न्यायाधीश।', en: 'Guru, professor, judge.' },
      business: { hi: 'विदेश व्यापार, प्रकाशन, कोचिंग।', en: 'Foreign trade, publishing, coaching.' },
      sports: { hi: 'धनुष, घुड़दौड़, आउटडोर।', en: 'Archery, equestrian, outdoor sports.' },
      spirituality: { hi: 'गुरु-सेवा, तीर्थ, उपनिषद् अध्ययन।', en: 'Guru seva, pilgrimage, Upanishad study.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'धनु लग्न - गुरु रंग; शरीर उदार, धर्म-प्रेमी।', en: 'Dhanu lagna - Jupiter colours; generous body, dharma-loving.' },
      moon: { hi: 'चंद्र धनु - आशावादी मन, यात्रा-चाह।', en: 'Moon in Sagittarius - optimistic mind, travel urge.' },
      sun: { hi: 'सूर्य धनु - धर्म-पहचान, नेता-वक्ता।', en: 'Sun in Sagittarius - dharma identity, speaker-leader.' },
    },
    archetypes: [
      { hi: 'गुरु', en: 'Guru' },
      { hi: 'यात्री', en: 'Traveller' },
      { hi: 'धनुर्धर', en: 'Archer' },
      { hi: 'प्रचारक', en: 'Preacher' },
      { hi: 'दार्शनिक', en: 'Philosopher' },
    ],
  },
  10: {
    sanskritMeaning: { hi: 'मकर = मगर/समुद्री जीव - कर्म, धैर्य, ऊँचाई।', en: 'Makara = sea-creature - karma, patience, heights.' },
    introLead: { hi: 'दशम राशि - पृथ्वी, शनि-स्वामी, कर्म-भाव। 270° – 300°।', en: 'Tenth sign - earth, Saturn-ruled, karma house. 270° – 300°.' },
    symbolism: [
      { hi: 'मकर - पानी से धरती की ओर - आरोहण।', en: 'Makara - from water toward land - ascent.' },
      { hi: 'शिखर और लक्ष्य - दीर्घकालिक कर्म।', en: 'Summit and goal - long-term karma.' },
      { hi: 'अनुशासन और संरचना।', en: 'Discipline and structure.' },
    ],
    mythology: [
      { hi: 'शनि - न्याय और कर्म के दाता।', en: 'Shani - giver of justice and karma.' },
      { hi: 'विष्णु का मत्स्य-कच्छप से मकर रूप - संरक्षण और आरोहण।', en: 'Vishnu’s fish-turtle to makara form - preservation and ascent.' },
    ],
    elementPositive: [
      { hi: 'अनुशासन और धैर्य', en: 'Discipline and patience' },
      { hi: 'दीर्घकालिक लक्ष्य', en: 'Long-term goals' },
      { hi: 'जिम्मेदारी', en: 'Responsibility' },
    ],
    elementNegative: [
      { hi: 'कठोरता और उदासी', en: 'Harshness and gloom' },
      { hi: 'अति काम', en: 'Workaholism' },
      { hi: 'भावनात्मक दूरी', en: 'Emotional distance' },
    ],
    swabhavWhy: { hi: 'चर - करियर में गति, ऊँचाई की ओर चढ़ना।', en: 'Chara - movement in career, climbing heights.' },
    swabhavExamples: [
      { hi: 'पदोन्नति की लालसा', en: 'Ambition for promotion' },
      { hi: 'कड़ी मेहनत', en: 'Hard work' },
      { hi: 'जिम्मेदारी लेना', en: 'Taking responsibility' },
    ],
    lordRows: [
      { qualityHi: 'अनुशासन', qualityEn: 'Discipline', exprHi: 'संरचना', exprEn: 'Structure' },
      { qualityHi: 'धैर्य', qualityEn: 'Patience', exprHi: 'दीर्घकालिक योजना', exprEn: 'Long plans' },
      { qualityHi: 'कर्म', qualityEn: 'Karma', exprHi: 'कर्तव्य', exprEn: 'Duty' },
      { qualityHi: 'न्याय', qualityEn: 'Justice', exprHi: 'परिणाम', exprEn: 'Results' },
    ],
    appearance: [
      { hi: 'पतला या दुबला शरीर', en: 'Lean or thin body' },
      { hi: 'गंभीर चेहरा', en: 'Serious face' },
      { hi: 'मजबूत हड्डियाँ', en: 'Strong bones' },
    ],
    strengths: [
      { hi: 'अनुशासित और धैर्यवान', en: 'Disciplined and patient' },
      { hi: 'रणनीतिक करियर निर्माण', en: 'Strategic career builder' },
      { hi: 'विश्वसनीय', en: 'Dependable' },
    ],
    challenges: [
      { hi: 'उदासी और संदेह', en: 'Pessimism and doubt' },
      { hi: 'अति काम-लिप्तता', en: 'Work obsession' },
      { hi: 'भावनात्मक शुष्कता', en: 'Emotional dryness' },
    ],
    kalapurushWhy: [
      { hi: 'मकर घुटने और अस्थि - संयम से चलना।', en: 'Makara rules knees and bones - walking with restraint.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 10th - कर्म, पद, प्रतिष्ठा।', en: 'Natural 10th - karma, status, reputation.' },
    ],
    relationships: {
      friendship: { hi: 'कम पर स्थिर; समय देना मुश्किल।', en: 'Few but steady; hard to find time.' },
      love: { hi: 'वफादार पर शुष्क; प्रतिबद्धता लंबी।', en: 'Loyal but dry; long commitment.' },
      conflict: { hi: 'चुप्पी या ठंडा व्यवहार।', en: 'Silence or cold behaviour.' },
      leadership: { hi: 'कार्यकारी, प्रशासक, CEO।', en: 'Executive, administrator, CEO.' },
    },
    planets: {
      Sun: { hi: 'कर्म-नेता, सरकारी सम्मान।', en: 'Karma-leader, government honour.' },
      Moon: { hi: 'उदास मन, जिम्मेदारी-भार।', en: 'Melancholy mind, responsibility burden.' },
      Mars: { hi: 'उच्च - अनुशासित साहस, इंजीनियरिंग।', en: 'Exalted - disciplined courage, engineering.' },
      Mercury: { hi: 'व्यावहारिक योजना, लेखा।', en: 'Practical planning, accounts.' },
      Jupiter: { hi: 'नीच - आस्था परीक्षा; कर्म से धर्म।', en: 'Debilitated - faith tested; dharma through work.' },
      Venus: { hi: 'काम में सौंदर्य की कमी संभव।', en: 'Beauty may suffer under work pressure.' },
      Saturn: { hi: 'स्वराशि - कर्म शिखर, धैर्य।', en: 'Own sign - peak karma, patience.' },
      Rahu: { hi: 'असामान्य करियर, विदेशी पद।', en: 'Unusual career, foreign status.' },
      Ketu: { hi: 'कर्म से वैराग्य या आध्यात्मिक कर्तव्य।', en: 'Detachment from career or spiritual duty.' },
    },
    dignityNotes: [
      { hi: 'मंगल उच्च, गुरु नीच मकर में।', en: 'Mars exalted, Jupiter debilitated in Capricorn.' },
      { hi: 'शनि स्वराशि - कर्म का घर।', en: 'Saturn own sign - home of karma.' },
    ],
    lifeStages: {
      childhood: { hi: 'गंभीर, जिम्मेदार, बड़ों जैसा।', en: 'Serious, responsible, old soul child.' },
      career: { hi: 'सरकार, इंजीनियरिंग, प्रबंधन, कानून।', en: 'Government, engineering, management, law.' },
      leadership: { hi: 'CEO, मंत्री, वरिष्ठ अधिकारी।', en: 'CEO, minister, senior officer.' },
      business: { hi: 'इन्फ्रास्ट्रक्चर, रियल एस्टेट, उद्योग।', en: 'Infrastructure, real estate, industry.' },
      sports: { hi: 'सहनशक्ति, पर्वतारोहण।', en: 'Endurance, mountaineering.' },
      spirituality: { hi: 'कर्म योग, शनि उपासना, तप।', en: 'Karma yoga, Shani worship, austerity.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'मकर लग्न - शनि रंग; शरीर गंभीर, कर्म-केंद्रित।', en: 'Makara lagna - Saturn colours; serious body, karma-focused.' },
      moon: { hi: 'चंद्र मकर - उदास मन, जिम्मेदारी।', en: 'Moon in Capricorn - sober mind, responsibility.' },
      sun: { hi: 'सूर्य मकर - अधिकार-पहचान, कर्तव्य।', en: 'Sun in Capricorn - authority identity, duty.' },
    },
    archetypes: [
      { hi: 'कार्यकारी', en: 'Executive' },
      { hi: 'शिखरारोही', en: 'Climber' },
      { hi: 'अनुशासक', en: 'Disciplinarian' },
      { hi: 'वास्तुकार', en: 'Architect' },
      { hi: 'कर्मयोगी', en: 'Karma yogi' },
    ],
  },
  11: {
    sanskritMeaning: { hi: 'कुम्भ = कलश - जल धारक, मानवता, विचार।', en: 'Kumbha = pitcher - water bearer, humanity, ideas.' },
    introLead: { hi: 'एकादश राशि - वायु, शनि-स्वामी, लाभ-भाव। 300° – 330°।', en: 'Eleventh sign - air, Saturn-ruled, gains house. 300° – 330°.' },
    symbolism: [
      { hi: 'घड़ा से जल बहाना - मानवता की सेवा।', en: 'Pouring water from jar - service to humanity.' },
      { hi: 'नेटवर्क और समूह।', en: 'Networks and groups.' },
      { hi: 'भविष्य और नवाचार।', en: 'Future and innovation.' },
    ],
    mythology: [
      { hi: 'शनि की वायु-शैली - संरचना + विचार।', en: 'Saturn’s airy style - structure plus ideas.' },
      { hi: 'समुद्र मंथन से अमृत घड़ा - कलश प्रतीक।', en: 'Amrita pot from churning - kumbha symbol.' },
    ],
    elementPositive: [
      { hi: 'मानवता और नवाचार', en: 'Humanity and innovation' },
      { hi: 'वस्तुनिष्ठ बुद्धि', en: 'Objective intellect' },
      { hi: 'समूह-लाभ', en: 'Group gains' },
    ],
    elementNegative: [
      { hi: 'भावनात्मक दूरी', en: 'Emotional distance' },
      { hi: 'अड़ियल विचार', en: 'Stubborn ideas' },
      { hi: 'विद्रोह बिना योजना', en: 'Rebellion without plan' },
    ],
    swabhavWhy: { hi: 'स्थिर - विचारों में अड़ियल, समूह में स्थिर।', en: 'Sthira - fixed in ideas, steady in groups.' },
    swabhavExamples: [
      { hi: 'सामाजिक कारण', en: 'Social causes' },
      { hi: 'तकनीकी नवाचार', en: 'Tech innovation' },
      { hi: 'मित्र-मंडली', en: 'Friend circles' },
    ],
    lordRows: [
      { qualityHi: 'विचार', qualityEn: 'Ideas', exprHi: 'नवाचार', exprEn: 'Innovation' },
      { qualityHi: 'संरचना', qualityEn: 'Structure', exprHi: 'सिस्टम', exprEn: 'Systems' },
      { qualityHi: 'मानवता', qualityEn: 'Humanity', exprHi: 'सेवा', exprEn: 'Service' },
      { qualityHi: 'विद्रोह', qualityEn: 'Reform', exprHi: 'परिवर्तन', exprEn: 'Change' },
    ],
    appearance: [
      { hi: 'अनोखा या भविष्यवादी स्टाइल', en: 'Unique or futuristic style' },
      { hi: 'लंबा कद', en: 'Tall stature' },
      { hi: 'शांत नेत्र', en: 'Calm eyes' },
    ],
    strengths: [
      { hi: 'दूरदर्शी और मानवतावादी', en: 'Visionary and humanitarian' },
      { hi: 'मित्र-नेटवर्क', en: 'Friend networks' },
      { hi: 'वस्तुनिष्ठ', en: 'Objective' },
    ],
    challenges: [
      { hi: 'भावनात्मक शुष्कता', en: 'Emotional coldness' },
      { hi: 'अड़ियल विचार', en: 'Fixed opinions' },
      { hi: 'समूह से अलग महसूस', en: 'Feeling apart from the crowd' },
    ],
    kalapurushWhy: [
      { hi: 'कुम्भ पिंडली और रक्त संचार - वायु-प्रवाह।', en: 'Kumbha rules calves and circulation - air flow.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 11th - लाभ, मित्र, इच्छा।', en: 'Natural 11th - gains, friends, desires.' },
    ],
    relationships: {
      friendship: { hi: 'विशाल मित्र-मंडल; समान विचार।', en: 'Wide friend circle; shared ideals.' },
      love: { hi: 'मित्रता से प्रेम; स्वतंत्रता।', en: 'Love from friendship; freedom needed.' },
      conflict: { hi: 'ठंडा तर्क; दूरी बना लेता है।', en: 'Cool logic; creates distance.' },
      leadership: { hi: 'सुधारक, समूह-नेता।', en: 'Reformer, group leader.' },
    },
    planets: {
      Sun: { hi: 'समूह में पहचान; असामान्य अहंकार।', en: 'Identity in groups; unusual ego.' },
      Moon: { hi: 'वस्तुनिष्ठ मन; भावना दूरी।', en: 'Objective mind; emotional distance.' },
      Mars: { hi: 'विद्रोही ऊर्जा, तकनीकी साहस।', en: 'Rebel energy, tech courage.' },
      Mercury: { hi: 'विज्ञान-बुद्धि, नेटवर्किंग।', en: 'Scientific mind, networking.' },
      Jupiter: { hi: 'मानवता-धर्म, बड़े विचार।', en: 'Humanitarian dharma, big ideas.' },
      Venus: { hi: 'अनोखी कला, असामान्य प्रेम।', en: 'Unique art, unusual love.' },
      Saturn: { hi: 'स्वराशि - संरचित सुधार।', en: 'Own sign - structured reform.' },
      Rahu: { hi: 'तकनीकी क्रांति, विदेशी नेटवर्क।', en: 'Tech revolution, foreign networks.' },
      Ketu: { hi: 'आध्यात्मिक विद्रोह, वैराग्य।', en: 'Spiritual rebellion, detachment.' },
    },
    dignityNotes: [{ hi: 'शनि स्वराशि - वायु में अनुशासन।', en: 'Saturn own sign - discipline in air.' }],
    lifeStages: {
      childhood: { hi: 'अलग, जिज्ञासु, गैजेट-प्रेमी।', en: 'Different, curious, gadget-loving.' },
      career: { hi: 'IT, विज्ञान, सामाजिक कार्य, एविएशन।', en: 'IT, science, social work, aviation.' },
      leadership: { hi: 'सुधारक, स्टार्टअप संस्थापक।', en: 'Reformer, startup founder.' },
      business: { hi: 'टेक, क्राउडफंड, NGO।', en: 'Tech, crowdfunding, NGOs.' },
      sports: { hi: 'टीम खेल, एडवेंचर स्पोर्ट्स।', en: 'Team sports, adventure sports.' },
      spirituality: { hi: 'मानवता-सेवा, ध्यान, योग।', en: 'Humanitarian seva, meditation, yoga.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'कुम्भ लग्न - शनि रंग; शरीर अलग, विचार-प्रधान।', en: 'Kumbha lagna - Saturn colours; unique body, idea-first.' },
      moon: { hi: 'चंद्र कुम्भ - वस्तुनिष्ठ मन, मित्र-प्रेम।', en: 'Moon in Aquarius - objective mind, loves friends.' },
      sun: { hi: 'सूर्य कुम्भ - सुधारक पहचान, समूह-नेता।', en: 'Sun in Aquarius - reformer identity, group leader.' },
    },
    archetypes: [
      { hi: 'सुधारक', en: 'Reformer' },
      { hi: 'वैज्ञानिक', en: 'Scientist' },
      { hi: 'मानवतावादी', en: 'Humanitarian' },
      { hi: 'नवप्रवर्तक', en: 'Innovator' },
      { hi: 'नेटवर्कर', en: 'Networker' },
    ],
  },
  12: {
    sanskritMeaning: { hi: 'मीन = मछली - दो विपरीत दिशाएँ, मोक्ष, करुणा।', en: 'Meena = fish - two directions, moksha, compassion.' },
    introLead: { hi: 'द्वादश राशि - जल, गुरु-स्वामी, व्यय-मोक्ष। 330° – 360°।', en: 'Twelfth sign - water, Jupiter-ruled, expense-moksha. 330° – 360°.' },
    symbolism: [
      { hi: 'दो मछलियाँ विपरीत - भौतिक और आध्यात्मिक धारा।', en: 'Two fish opposite - material and spiritual streams.' },
      { hi: 'समुद्र - अनंत, स्वप्न, मोक्ष।', en: 'Ocean - infinity, dreams, moksha.' },
      { hi: 'करुणा और त्याग।', en: 'Compassion and surrender.' },
    ],
    mythology: [
      { hi: 'विष्णु का मत्स्य अवतार - प्रलय से रक्षा।', en: 'Vishnu’s Matsya avatar - rescue from flood.' },
      { hi: 'गुरु की जल-शैली - ज्ञान और करुणा।', en: 'Jupiter’s water style - wisdom and compassion.' },
    ],
    elementPositive: [
      { hi: 'करुणा और कल्पना', en: 'Compassion and imagination' },
      { hi: 'आध्यात्मिक झुकाव', en: 'Spiritual inclination' },
      { hi: 'कला और संगीत', en: 'Art and music' },
    ],
    elementNegative: [
      { hi: 'भ्रम और पलायन', en: 'Confusion and escapism' },
      { hi: 'सीमा की कमी', en: 'Poor boundaries' },
      { hi: 'दुनिया से कटना', en: 'Withdrawal from world' },
    ],
    swabhavWhy: { hi: 'द्विस्वभाव - भौतिक और आध्यात्मिक के बीच तैरना।', en: 'Dwiswabhava - swimming between material and spiritual.' },
    swabhavExamples: [
      { hi: 'स्वप्न और कल्पना', en: 'Dreams and fantasy' },
      { hi: 'दान और त्याग', en: 'Charity and surrender' },
      { hi: 'विदेश या एकांत', en: 'Foreign lands or solitude' },
    ],
    lordRows: [
      { qualityHi: 'ज्ञान', qualityEn: 'Wisdom', exprHi: 'करुणा', exprEn: 'Compassion' },
      { qualityHi: 'कल्पना', qualityEn: 'Imagination', exprHi: 'कला', exprEn: 'Art' },
      { qualityHi: 'मोक्ष', qualityEn: 'Moksha', exprHi: 'वैराग्य', exprEn: 'Detachment' },
      { qualityHi: 'त्याग', qualityEn: 'Surrender', exprHi: 'सेवा', exprEn: 'Service' },
    ],
    appearance: [
      { hi: 'कोमल, स्वप्निल नेत्र', en: 'Soft, dreamy eyes' },
      { hi: 'पैर प्रभावशाली', en: 'Notable feet' },
      { hi: 'शांत चाल', en: 'Gentle gait' },
    ],
    strengths: [
      { hi: 'करुणामय और कल्पनाशील', en: 'Compassionate and imaginative' },
      { hi: 'आध्यात्मिक संवेदनशीलता', en: 'Spiritually sensitive' },
      { hi: 'कलात्मक प्रतिभा', en: 'Artistic talent' },
    ],
    challenges: [
      { hi: 'भ्रम और पलायन', en: 'Confusion and escapism' },
      { hi: 'सीमा का अभाव', en: 'Lack of boundaries' },
      { hi: 'व्यसन की संवेदनशीलता', en: 'Vulnerability to addiction' },
    ],
    kalapurushWhy: [
      { hi: 'मीन पैर - चलने का अंत, मोक्ष-प्रतीक।', en: 'Meena rules feet - end of walking, moksha symbol.' },
    ],
    purusharthas: [
      { hi: 'प्राकृतिक 12th - व्यय, मोक्ष, विदेश।', en: 'Natural 12th - expense, moksha, foreign lands.' },
    ],
    relationships: {
      friendship: { hi: 'करुणामय मित्र; सीमाएँ धुंधली।', en: 'Compassionate friend; blurry boundaries.' },
      love: { hi: 'रोमांटिक स्वप्न; त्याग-प्रेम।', en: 'Romantic dream; sacrificial love.' },
      conflict: { hi: 'पलायन या मौन; दुख अंदर।', en: 'Escapes or goes silent; hurts inside.' },
      leadership: { hi: 'प्रेरक कलाकार, आध्यात्मिक मार्गदर्शक।', en: 'Inspiring artist, spiritual guide.' },
    },
    planets: {
      Sun: { hi: 'गुप्त अहंकार, आध्यात्मिक प्रकाश।', en: 'Hidden ego, spiritual light.' },
      Moon: { hi: 'स्वराशि - गहरा कल्पनाशील मन।', en: 'Own sign - deep imaginative mind.' },
      Mars: { hi: 'कमजोर सीधी ऊर्जा; आध्यात्मिक साहस।', en: 'Weak direct drive; spiritual courage.' },
      Mercury: { hi: 'नीच - भ्रमित विचार; कल्पना तीव्र।', en: 'Debilitated - confused thought; vivid fantasy.' },
      Jupiter: { hi: 'स्वराशि - करुणा-ज्ञान, मोक्ष झुकाव।', en: 'Own sign - compassionate wisdom, moksha bent.' },
      Venus: { hi: 'उच्च - कला, प्रेम, सौंदर्य शिखर।', en: 'Exalted - peak art, love, beauty.' },
      Saturn: { hi: 'गंभीर वैराग्य, एकांत साधना।', en: 'Serious detachment, solitary sadhana.' },
      Rahu: { hi: 'विदेश, भ्रम, असामान्य आध्यात्म।', en: 'Foreign lands, illusion, unusual spirituality.' },
      Ketu: { hi: 'मोक्ष-प्रवृत्ति, पूर्व जन्म स्मृति।', en: 'Moksha drive, past-life memory.' },
    },
    dignityNotes: [
      { hi: 'शुक्र उच्च, बुध नीच मीन में।', en: 'Venus exalted, Mercury debilitated in Pisces.' },
      { hi: 'गुरु स्वराशि - मोक्ष-ज्ञान।', en: 'Jupiter own sign - moksha wisdom.' },
    ],
    lifeStages: {
      childhood: { hi: 'स्वप्निल, कल्पनाशील, संवेदनशील।', en: 'Dreamy, imaginative, sensitive child.' },
      career: { hi: 'कला, संगीत, चिकित्सा, आध्यात्म, समुद्र।', en: 'Arts, music, healing, spirituality, ocean work.' },
      leadership: { hi: 'गुरु, कलाकार, परोपकारी।', en: 'Guru, artist, philanthropist.' },
      business: { hi: 'फिल्म, फैशन, चैरिटी, स्पा।', en: 'Film, fashion, charity, spa wellness.' },
      sports: { hi: 'जल खेल, योग, नृत्य।', en: 'Water sports, yoga, dance.' },
      spirituality: { hi: 'भक्ति, ध्यान, मोक्ष-साधना।', en: 'Bhakti, meditation, moksha sadhana.' },
    },
    lagnaVsMoon: {
      lagna: { hi: 'मीन लग्न - गुरु रंग; शरीर कोमल, आध्यात्म-झुकाव।', en: 'Meena lagna - Jupiter colours; soft body, spiritual bent.' },
      moon: { hi: 'चंद्र मीन - स्वप्निल मन; स्वराशि बल।', en: 'Moon in Pisces - dreamy mind; own-sign strength.' },
      sun: { hi: 'सूर्य मीन - करुणा-पहचान, कलाकार आत्मा।', en: 'Sun in Pisces - compassionate identity, artist soul.' },
    },
    archetypes: [
      { hi: 'संत', en: 'Saint' },
      { hi: 'कलाकार', en: 'Artist' },
      { hi: 'हेलर', en: 'Healer' },
      { hi: 'स्वप्नदर्शी', en: 'Dreamer' },
      { hi: 'मोक्षखोजी', en: 'Moksha seeker' },
    ],
  },
};
